<template>
  <div class="auth-page">
    <div class="auth-container">
      <div class="auth-logo">
        <a href="/">
          <img :src="logoUrl" alt="SMASHING SHAPE" />
        </a>
      </div>
      
      <div class="auth-card">
        <div class="auth-header">
          <h1>Verify Your Email</h1>
          <p>We've sent a 6-digit code to <strong>{{ email }}</strong></p>
        </div>
        
        <form @submit.prevent="handleVerify" class="auth-form">
          <div class="form-group">
            <label>Verification Code</label>
            <div class="otp-input-container">
              <input 
                v-for="(digit, index) in 6" 
                :key="index"
                :ref="'otpInput' + index"
                type="text" 
                maxlength="1"
                pattern="[0-9]"
                inputmode="numeric"
                class="otp-box"
                :value="codeDigits[index]"
                @input="handleOtpInput($event, index)"
                @keydown="handleOtpKeydown($event, index)"
                @paste="handlePaste($event)"
              >
            </div>
            <span v-if="error" class="error">{{ error }}</span>
          </div>

          <button type="submit" class="btn-submit" :disabled="loading || code.length !== 6">
            {{ loading ? 'Verifying...' : 'Verify Email' }}
          </button>
        </form>

        <div class="auth-footer">
          <p>Didn't receive the code? 
            <a href="#" @click.prevent="resendCode" :class="{ disabled: resending }">
              {{ resending ? 'Sending...' : 'Resend Code' }}
            </a>
          </p>
        </div>

        <div v-if="successMessage" class="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {{ successMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VerifyEmail',
  props: {
    email: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      logoUrl: '/logos/logodark.svg',
      code: '',
      loading: false,
      resending: false,
      error: '',
      successMessage: ''
    }
  },
  computed: {
    codeDigits() {
      return this.code.split('').concat(Array(6).fill('')).slice(0, 6)
    }
  },
  methods: {
    handleOtpInput(event, index) {
      const value = event.target.value.replace(/[^0-9]/g, '')
      
      if (value) {
        const digits = this.code.split('')
        digits[index] = value.charAt(0)
        this.code = digits.join('').replace(/undefined/g, '')
        
        // Move to next input
        if (index < 5) {
          this.$nextTick(() => {
            const nextInput = this.$refs['otpInput' + (index + 1)]
            if (nextInput && nextInput[0]) nextInput[0].focus()
          })
        }
      }
      event.target.value = value.charAt(0)
    },
    
    handleOtpKeydown(event, index) {
      // Handle backspace
      if (event.key === 'Backspace') {
        const digits = this.code.split('')
        if (!digits[index] && index > 0) {
          // Move to previous input if current is empty
          this.$nextTick(() => {
            const prevInput = this.$refs['otpInput' + (index - 1)]
            if (prevInput && prevInput[0]) {
              prevInput[0].focus()
              prevInput[0].select()
            }
          })
        }
        digits[index] = ''
        this.code = digits.join('')
      }
      
      // Handle arrow keys
      if (event.key === 'ArrowLeft' && index > 0) {
        this.$refs['otpInput' + (index - 1)][0].focus()
      }
      if (event.key === 'ArrowRight' && index < 5) {
        this.$refs['otpInput' + (index + 1)][0].focus()
      }
    },
    
    handlePaste(event) {
      event.preventDefault()
      const pastedData = event.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6)
      this.code = pastedData
      
      // Focus on appropriate input
      const focusIndex = Math.min(pastedData.length, 5)
      this.$nextTick(() => {
        const input = this.$refs['otpInput' + focusIndex]
        if (input && input[0]) input[0].focus()
      })
    },

    async handleVerify() {
      this.loading = true
      this.error = ''
      
      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
        
        const response = await fetch(`/onboarding/${encodeURIComponent(this.email)}/verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'Accept': 'application/json'
          },
          body: JSON.stringify({ code: this.code })
        })
        
        const data = await response.json()
        
        if (response.ok) {
          this.successMessage = 'Email verified! Redirecting...'
          setTimeout(() => {
            window.location.href = data.redirect || '/onboarding/address'
          }, 500)
        } else {
          this.error = data.errors?.code?.[0] || data.message || 'Invalid verification code'
        }
      } catch (err) {
        this.error = 'An error occurred. Please try again.'
      } finally {
        this.loading = false
      }
    },
    
    async resendCode() {
      this.resending = true
      this.error = ''
      
      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
        
        const response = await fetch(`/onboarding/${encodeURIComponent(this.email)}/resend`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'Accept': 'application/json'
          },
          credentials: 'same-origin'
        })
        
        if (response.ok) {
          this.successMessage = 'A new verification code has been sent to your email.'
          setTimeout(() => { this.successMessage = '' }, 5000)
        } else {
          this.error = 'Failed to resend code. Please try again.'
        }
      } catch (err) {
        this.error = 'An error occurred. Please try again.'
      } finally {
        this.resending = false
      }
    }
  }
}
</script>

